/**
 * @voltxampere/engine — Op-amp AC small-signal stamp (Sprint 22 D1)
 *
 * Sprint 20 shipped a VCVS-style augmented-row stamp, but the Sprint 7
 * DC stamp uses a Norton form (no augmented row, internal Rout = 1 Ω).
 * The mismatch surfaced as D-20-9: the AC stamp called
 * `ctx.vSourceIndex(component.id)` on a component the planner had
 * deliberately not registered as a voltage source (its DC
 * `augments=[]`), so any AC sweep over a circuit containing an op-amp
 * threw `vSourceIndex: component "..." is not registered`.
 *
 * Sprint 22 D1 closes the gap by redesigning the AC stamp to mirror
 * the DC Norton model. The output node sees:
 *
 *   Y[out, out]   += 1/Rout          (frequency-flat, real)
 *   Y[out, in_pos] -= A(jω) / Rout    (complex)
 *   Y[out, in_neg] += A(jω) / Rout    (complex)
 *
 * with `A(jω) = A0 / (1 + jω/ω_p)`. No augmented MNA row, no RHS
 * contribution — small-signal AC has no independent stimulus.
 *
 * Pole frequency selection (D-22-4):
 *   - if `params.gainBandwidthProduct` is set → ω_p = 2π · GBW / A0
 *   - else → ω_p = 2π · poleFrequency
 *
 * Saturation is intentionally not modelled in AC (linearisation
 * around the DC OP is the small-signal premise).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { getNumericParam, getTerminalNetId } from "../terminal-utils.js";
const ROUT = 1;
export const opampACStamp = (ctx, component) => {
    const A0 = getNumericParam(component, "openLoopGain", 1e5);
    if (!(A0 > 0) || !Number.isFinite(A0)) {
        throw new StampParameterError(`opampACStamp: component "${component.id}" has non-positive openLoopGain (got ${String(A0)})`, {
            meta: {
                stampType: "opamp",
                parameter: "openLoopGain",
                received: A0,
                expected: "> 0 and finite",
                componentId: component.id,
            },
        });
    }
    // GBW wins when both fields are present — the AC math is identical
    // either way, but `gainBandwidthProduct` is the more recognisable
    // datasheet figure.
    const params = component.params;
    const gbwRaw = params.gainBandwidthProduct;
    const poleHz = typeof gbwRaw === "number" && gbwRaw > 0 && Number.isFinite(gbwRaw)
        ? gbwRaw / A0
        : getNumericParam(component, "poleFrequency", 10);
    if (!(poleHz > 0) || !Number.isFinite(poleHz)) {
        throw new StampParameterError(`opampACStamp: component "${component.id}" has non-positive pole frequency (got ${String(poleHz)})`, {
            meta: {
                stampType: "opamp",
                parameter: "poleFrequency",
                received: poleHz,
                expected: "> 0 and finite",
                componentId: component.id,
            },
        });
    }
    const inPos = ctx.nodeIndex(getTerminalNetId(component, "in_pos"));
    const inNeg = ctx.nodeIndex(getTerminalNetId(component, "in_neg"));
    const outNode = ctx.nodeIndex(getTerminalNetId(component, "out"));
    // A(jω) = A0 / (1 + jω/ω_p)
    // Rationalise: multiply numerator + denominator by 1 - jω/ω_p
    //   A_re =  A0 / (1 + x²)
    //   A_im = -A0·x / (1 + x²)   with x = ω / ω_p
    const omegaP = 2 * Math.PI * poleHz;
    const x = ctx.omega / omegaP;
    const denom = 1 + x * x;
    const aRe = A0 / denom;
    const aIm = (-A0 * x) / denom;
    const invRout = 1 / ROUT;
    // Output Norton stamp — mirrors the DC opamp's structure.
    ctx.addY(outNode, outNode, invRout, 0);
    ctx.addY(outNode, inPos, -aRe * invRout, -aIm * invRout);
    ctx.addY(outNode, inNeg, aRe * invRout, aIm * invRout);
    // No RHS contribution — small-signal AC has no source term.
};
//# sourceMappingURL=opamp.js.map